/** Map menu node id/guid → hub list view (query ?view=). */
export const PROJECT_WORKSPACE_MENU_VIEWS = {
  "11325": "checkNew",
  "11326": "inProcess",
  "11363": "all",
  "11307": "checkNew",
  "11308": "inProcess",
  "19482": "all",
  "19497": "checkNew",
};

const LEGACY_INBOX_LINKS = new Set([
  "CheckNewRequest",
  "InProcessRequest",
  "AllRequests",
  "AllRequest",
  "Request",
  "ProjectWorkspace",
]);

/** ProjectWorkspace hub exists only for operator / management cartables. */
const PROJECT_WORKSPACE_AREA_PATTERN = /\/SLN\/(SolarentOperator|Management)(\/|$)/;

const isProjectWorkspaceAreaLink = (link) =>
  typeof link === "string" && PROJECT_WORKSPACE_AREA_PATTERN.test(link);

export const defaultViewForLegacyLink = (oldLink) => {
  switch (oldLink) {
    case "CheckNewRequest":
    case "Request":
      return "checkNew";
    case "InProcessRequest":
      return "inProcess";
    case "AllRequests":
    case "AllRequest":
      return "all";
    default:
      return "inProcess";
  }
};

/** Canonical links for Lead Campaign leaf menus (MNG_ModuleLists ids). */
export const LEAD_CAMPAIGN_MENU_CANONICAL_LINKS = {
  19513: "/SLN/MarketGrowth/LeadCampaigns",
  19555: "/SLN/MarketGrowth/LeadCampaigns/Dashboard",
  19556: "/SLN/MarketGrowth/LeadCampaigns/Groups",
  19557: "/SLN/MarketGrowth/LeadCampaigns/Campaigns",
  19558: "/SLN/MarketGrowth/LeadCampaigns/Reports",
};

/** Flat segments under /SLN/MarketGrowth that map to LeadCampaigns subtree. */
export const MARKET_GROWTH_PATH_ALIASES = {
  groups: ["groups", "leadcampaigns/groups"],
  campaigns: ["campaigns", "leadcampaigns/campaigns"],
  reports: ["reports", "leadcampaigns/reports"],
  leadcampaigns: ["leadcampaigns"],
};

export const applyMarketGrowthMenuLinks = (menuList) => {
  const visited = new WeakSet();

  const walk = (items, depth = 0) => {
    if (!Array.isArray(items) || depth > 12) return;

    items.forEach((menu) => {
      if (!menu || typeof menu !== "object") return;
      if (visited.has(menu)) return;
      visited.add(menu);

      const key = String(menu.guid || menu.id || "");
      const canonical = LEAD_CAMPAIGN_MENU_CANONICAL_LINKS[key];
      if (canonical) {
        menu.oldLink = menu.oldLink || menu.link;
        menu.link = canonical;
      }

      walk(menu.child, depth + 1);
    });
  };

  walk(menuList);
  return menuList;
};

/** After menu URLs are built, point operational inbox items to ProjectWorkspace hub. */
export const applyProjectWorkspaceMenuLinks = (menuList) => {
  const visited = new WeakSet();

  const walk = (items, depth = 0) => {
    if (!Array.isArray(items) || depth > 12) return;
    items.forEach((menu) => {
      if (!menu || typeof menu !== "object") return;
      if (visited.has(menu)) return;
      visited.add(menu);

      const key = String(menu.guid || menu.id || "");
      const oldLink = menu.oldLink || menu.link?.split("/").pop()?.split("?")[0];

      if (
        oldLink &&
        LEGACY_INBOX_LINKS.has(oldLink) &&
        typeof menu.link === "string" &&
        isProjectWorkspaceAreaLink(menu.link)
      ) {
        const view =
          PROJECT_WORKSPACE_MENU_VIEWS[key] || defaultViewForLegacyLink(oldLink);
        const base = menu.link.replace(
          /\/(CheckNewRequest|InProcessRequest|AllRequests|AllRequest|Request|ProjectWorkspace)(\?.*)?$/,
          ""
        );
        // بررسی اولیه must keep its dedicated cartable route (not ProjectWorkspace hub).
        if (view === "checkNew") {
          menu.link = `${base}/CheckNewRequest`;
        } else {
          menu.link = `${base}/ProjectWorkspace?view=${view}`;
        }
      }

      walk(menu.child, depth + 1);
    });
  };

  walk(menuList);
  return menuList;
};

/** Protected route: legacy inbox paths share access with ProjectWorkspace. */
export const PROJECT_WORKSPACE_PATH_ALIASES = {
  projectworkspace: [
    "projectworkspace",
    "checknewrequest",
    "inprocessrequest",
    "allrequests",
    "allrequest",
    "request",
  ],
  checknewrequest: ["checknewrequest", "projectworkspace"],
  inprocessrequest: ["inprocessrequest", "projectworkspace"],
  allrequests: ["allrequests", "projectworkspace"],
  allrequest: ["allrequest", "projectworkspace"],
  request: ["request", "projectworkspace"],
};

const menuLinkSegmentCandidates = (menu) => {
  const candidates = new Set();
  if (!menu) return candidates;

  const old = menu.oldLink;
  if (old) candidates.add(String(old).toLowerCase());

  if (typeof menu.link === "string") {
    const last = menu.link.split("/").filter(Boolean).pop()?.split("?")[0];
    if (last) candidates.add(last.toLowerCase());
  }

  return candidates;
};

export const menuMatchesPathSegment = (menu, segment) => {
  if (!segment || !menu) return false;

  const lower = segment.toLowerCase();
  const aliases = [
    ...(MARKET_GROWTH_PATH_ALIASES[lower] || [lower]),
    ...(PROJECT_WORKSPACE_PATH_ALIASES[lower] || []),
  ];
  const uniqueAliases = [...new Set(aliases.map((a) => a.toLowerCase()))];
  const candidates = menuLinkSegmentCandidates(menu);
  return uniqueAliases.some((alias) => candidates.has(alias));
};

const normalizePathForCompare = (pathname) =>
  String(pathname || "")
    .replace(/\/+$/, "")
    .toLowerCase();

/** True when pathname matches any menu.link prefix (case-insensitive). */
export const pathnameMatchesMenuTree = (pathname, menuList) => {
  const normalized = normalizePathForCompare(pathname);
  if (!normalized || normalized === "/") return false;

  let matched = false;
  const walk = (nodes, depth = 0) => {
    if (!Array.isArray(nodes) || matched || depth > 12) return;

    for (const node of nodes) {
      const link = normalizePathForCompare(node?.link);
      if (
        link &&
        (normalized === link || normalized.startsWith(`${link}/`))
      ) {
        matched = true;
        return;
      }
      walk(node.child, depth + 1);
    }
  };

  walk(menuList);
  return matched;
};

/** Redirect /SLN/customer → /SLN/Customer using menu link casing when available. */
export const resolveCanonicalMenuPath = (pathname, menuList) => {
  const current = String(pathname || "").replace(/\/+$/, "");
  const normalized = normalizePathForCompare(current);
  if (!normalized.startsWith("/sln")) return pathname;

  let bestMatch = null;
  const walk = (nodes, depth = 0) => {
    if (!Array.isArray(nodes) || depth > 12) return;

    for (const node of nodes) {
      const link = String(node?.link || "").replace(/\/+$/, "");
      const linkLower = link.toLowerCase();
      if (
        link &&
        (normalized === linkLower || normalized.startsWith(`${linkLower}/`)) &&
        (!bestMatch || link.length > bestMatch.length)
      ) {
        bestMatch = link;
      }
      walk(node.child, depth + 1);
    }
  };

  walk(menuList);
  if (!bestMatch || normalizePathForCompare(bestMatch) !== normalized) {
    return pathname;
  }

  if (current === bestMatch) return pathname;

  const suffix = current.slice(bestMatch.length);
  return `${bestMatch}${suffix}`;
};
